import React, { useEffect, useState } from 'react';

import cn from 'classnames';
import { connect, ConnectedProps, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { RootState } from '../../../../../init-store';
import StartScreenPopup from '../../../../shared/components/start-screen-popup/start-screen-popup';
import { toPassport } from '../../../../shared/features/user';
import { createLogger } from '../../../../shared/helpers/loggerFacade';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { PlanBroadcastHistoryState } from '../../../../shared/helpers/navigation/routing';
import { onErrorPlanning, onSuccessPlanning } from '../../features/plan-call/slice';
import Loader from '../loader';
import { isIframeSize, isValidMessage, isValidUrl } from './helpers';
import style from './index.css';
import { EventTypes, IframeSize, PostMessage } from './types';

const logger = createLogger('plan-call');

const mapDispatch = {
    onSuccessPlanning,
    onErrorPlanning,
};

const mapState = ({ sessionId, config: { calendarHost, appLang } }: RootState) => ({
    sessionId,
    calendarHost,
    appLang,
});

const MAX_LOAD_TIME = 5000;

const connector = connect(mapState, mapDispatch);

type Props = ConnectedProps<typeof connector> & {
    isBroadcast?: boolean;
};

const PlanCall: React.FC<Props> = ({
    onErrorPlanning,
    onSuccessPlanning,
    sessionId,
    calendarHost,
    appLang,
    isBroadcast,
}) => {
    const { state } = useLocation<PlanBroadcastHistoryState | null>();

    const [loading, setLoading] = useState(true);
    const [iframeSize, setIframeSize] = useState<IframeSize>({});
    const user = useSelector((state) => state.user);
    const dispatch = useDispatch();

    // Для русской и украинской локали включаем в календаре русский язык. Для остальных языков - английский.
    const calendarLang = ['ru', 'uk'].includes(appLang) ? 'ru' : 'en';

    const iframeUrl = new URL(
        `${calendarHost}/embed/create-event?platform=iframe&lang=${calendarLang}&telemost-app=1&telemost-web=1`
    );

    if (isBroadcast) {
        iframeUrl.searchParams.set('is-broadcast', '1');
    }

    if (state?.caption) {
        iframeUrl.searchParams.set('name', state.caption);
    }

    if (state?.description) {
        iframeUrl.searchParams.set('description', state.description);
    }

    if (state?.accessLevel) {
        iframeUrl.searchParams.set('telemost-access-level', state.accessLevel);
    }

    useEffect(() => {
        if (!user) {
            dispatch(toPassport());
        }
    }, [user, dispatch]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            onErrorPlanning({ isBroadcast });
        }, MAX_LOAD_TIME);

        const handlePostMessage = (event: MessageEvent) => {
            if (event.origin !== iframeUrl.origin) {
                return;
            }

            let message: PostMessage;

            try {
                message = JSON.parse(event.data) as PostMessage;
            } catch {
                return;
            }

            if (!isValidMessage(message)) {
                return;
            }

            switch (message.event) {
                case EventTypes.DOMReady:
                    setLoading(false);
                    clearTimeout(timeout);
                    break;

                case EventTypes.MORE_PARAMS_CLICK_CREATE_EVENT:
                    if (!message.payload || typeof message.payload !== 'string' || !isValidUrl(message.payload)) {
                        logger.error('Invalid message payload from calendar iframe', {
                            method: 'handlePostMessage',
                            level: 'error',
                            additional: {
                                event: EventTypes.MORE_PARAMS_CLICK_CREATE_EVENT,
                                payload: message.payload,
                            },
                        });
                        onErrorPlanning({ isBroadcast });
                        return;
                    }
                    window.location.href = message.payload;
                    break;

                case EventTypes.SUCCESS_CREATE_EVENT:
                    if (!message.payload || typeof message.payload !== 'string' || !isValidUrl(message.payload)) {
                        logger.error('Invalid message payload from calendar iframe', {
                            method: 'handlePostMessage',
                            level: 'error',
                            additional: {
                                event: EventTypes.SUCCESS_CREATE_EVENT,
                                payload: message.payload,
                            },
                        });
                        onErrorPlanning({ isBroadcast });
                        return;
                    }
                    onSuccessPlanning({
                        url: String(message.payload),
                        sessionId,
                        isBroadcast,
                    });
                    break;

                case EventTypes.ERROR_CREATE_EVENT:
                    onErrorPlanning({ isBroadcast });
                    break;

                case EventTypes.CONTENT_SIZE_CHANGED_CREATE_EVENT:
                    if (!message.payload || !isIframeSize(message.payload)) {
                        logger.error('Invalid message payload from calendar iframe', {
                            method: 'handlePostMessage',
                            level: 'error',
                            additional: {
                                event: EventTypes.CONTENT_SIZE_CHANGED_CREATE_EVENT,
                                payload: message.payload,
                            },
                        });
                        break;
                    }
                    setIframeSize(message.payload);
                    break;

                default:
                // no-op
            }
        };

        window.addEventListener('message', handlePostMessage);

        return () => {
            window.removeEventListener('message', handlePostMessage);
            clearTimeout(timeout);
        };
    }, [onErrorPlanning, onSuccessPlanning, sessionId, isBroadcast, iframeUrl.origin]);

    useEffect(() => {
        countMetrika('main_screen', isBroadcast ? 'plan_stream' : 'planning_screen', 'show', sessionId);
    }, [sessionId, isBroadcast]);

    return (
        <StartScreenPopup className={style.startScreenPopup}>
            {loading && <div className={style.loader}>{<Loader />}</div>}
            <div className={cn(style.iframeWrapper, { [style.hidden]: loading })} style={iframeSize}>
                <iframe className={cn(style.iframe)} style={iframeSize} src={iframeUrl.toString()} />
            </div>
        </StartScreenPopup>
    );
};

export const PlanCallPage = connector(PlanCall);
